/**
 * Staff calendar payment-choice helpers.
 *
 * Determines whether the after-Save payment step appears, which options
 * are available for the current clinic/subclinic, and which klippekort
 * actually apply to the selected service. Mirrors existing punch-card
 * and booking-policy rules — it does not invent a second payment system.
 */

import {
  paymentMethodIsEnabled,
  resolveChargeAmountCents,
  resolvePaymentTiming,
  resolvePrepaymentRequired,
  type BookingPaymentMethods,
  type PaymentTiming,
} from '@/lib/bookingPolicies';
import { resolvePaymentOptions } from '@/lib/paymentMethodResolver';
import { punchCardAppliesToService } from '@/lib/punchCardEligibility';

export { punchCardAppliesToService };

export type StaffPaymentChoice =
  | 'punch_card'
  | 'card'
  | 'mobilepay'
  | 'payment_link'
  | 'cash'
  | 'bank'
  | 'invoice_email';

export type PortalBookingPaymentChoice = 'punch_card' | 'card' | 'mobilepay' | 'later';

export interface PunchCardApplicabilityInput {
  id?: string;
  status?: string | null;
  remaining_clips: number;
  expires_at?: string | null;
  appointment_types?: unknown;
}

const PATIENT_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isPunchCardUsableNow(
  card: PunchCardApplicabilityInput,
  now: Date = new Date(),
): boolean {
  if (card.status && card.status !== 'active') return false;
  if (card.remaining_clips <= 0) return false;
  if (card.expires_at) {
    const expiresAt = new Date(card.expires_at);
    if (!Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() <= now.getTime()) {
      return false;
    }
  }
  return true;
}

export function selectApplicablePunchCards<T extends PunchCardApplicabilityInput>(
  cards: T[],
  serviceId: string | null,
  now: Date = new Date(),
): T[] {
  return cards.filter(
    (card) =>
      isPunchCardUsableNow(card, now) &&
      punchCardAppliesToService(card.appointment_types, serviceId),
  );
}

export function totalApplicableClips(cards: Array<{ remaining_clips: number }>): number {
  return cards.reduce((sum, card) => sum + Math.max(0, card.remaining_clips), 0);
}

export function pickPreferredPunchCard<T extends PunchCardApplicabilityInput>(
  cards: T[],
): T | null {
  if (cards.length === 0) return null;
  return (
    [...cards].sort((a, b) => {
      if (!a.expires_at && !b.expires_at) return 0;
      if (!a.expires_at) return 1;
      if (!b.expires_at) return -1;
      return new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
    })[0] ?? null
  );
}

export function isClinioPayAvailableForStaff(input: {
  useClinioPay?: boolean | null;
  payDisplayStatus?: string | null;
}): boolean {
  if (input.useClinioPay === false) return false;
  return input.payDisplayStatus === 'active';
}

export function hasValidPatientEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return PATIENT_EMAIL_PATTERN.test(email.trim());
}

export function resolvePortalBookingPaymentChoices(input: {
  methods: BookingPaymentMethods;
  applicablePunchCards: Array<{ remaining_clips: number }>;
  requiresPrepayment: boolean;
  chargeAmountCents: number;
}): PortalBookingPaymentChoice[] {
  const choices: PortalBookingPaymentChoice[] = [];
  if (
    paymentMethodIsEnabled(input.methods, 'punch_card') &&
    input.applicablePunchCards.some((card) => card.remaining_clips > 0)
  ) {
    choices.push('punch_card');
  }
  if (input.chargeAmountCents > 0) {
    if (paymentMethodIsEnabled(input.methods, 'card')) {
      choices.push('card');
    }
    if (paymentMethodIsEnabled(input.methods, 'mobilepay')) {
      choices.push('mobilepay');
    }
  }
  if (!input.requiresPrepayment && choices.length > 0) {
    choices.push('later');
  }
  return choices;
}

/** Clinic payment_timing wins over list order (later is last in `choices`). */
export function pickDefaultPortalPaymentChoice(
  choices: readonly PortalBookingPaymentChoice[],
  paymentTiming: PaymentTiming | string | null | undefined,
): PortalBookingPaymentChoice | null {
  if (choices.length === 0) return null;
  if (paymentTiming !== 'upon_booking' && choices.includes('later')) {
    return 'later';
  }
  return choices.find((choice) => choice !== 'later') ?? choices[0] ?? null;
}

/** Put the clinic default first so the selected radio card matches the mockup. */
export function orderPaymentChoicesDefaultFirst(
  choices: readonly PortalBookingPaymentChoice[],
  defaultChoice: PortalBookingPaymentChoice | null,
): PortalBookingPaymentChoice[] {
  if (!defaultChoice || !choices.includes(defaultChoice)) return [...choices];
  return [defaultChoice, ...choices.filter((choice) => choice !== defaultChoice)];
}

/**
 * Shared portal/online-booking payment context.
 *
 * Timing (when) is resolved first, independently of method (how).
 * Eligible klippekort are then offered as a payment method — including
 * when payment is required before the appointment is confirmed.
 */
export function resolvePortalBookingPaymentContext(input: {
  clinicPaymentTiming?: unknown;
  servicePaymentTiming?: unknown;
  serviceRequirePrepayment?: boolean | null;
  depositAmountCents?: number | null;
  priceCents?: number | null;
  methods: BookingPaymentMethods;
  applicablePunchCards: Array<{ remaining_clips: number }>;
}): {
  paymentTiming: PaymentTiming;
  chargeAmountCents: number;
  advancePaymentRequired: boolean;
  choices: PortalBookingPaymentChoice[];
} {
  const paymentTiming = resolvePaymentTiming({
    clinicPaymentTiming: input.clinicPaymentTiming,
    servicePaymentTiming: input.servicePaymentTiming,
    serviceRequirePrepayment: input.serviceRequirePrepayment,
  });
  const chargeAmountCents = resolveChargeAmountCents({
    depositAmountCents: input.depositAmountCents,
    priceCents: input.priceCents,
  });
  const advancePaymentRequired = resolvePrepaymentRequired({
    paymentTiming,
    depositAmountCents: input.depositAmountCents,
    priceCents: input.priceCents,
  });
  return {
    paymentTiming,
    chargeAmountCents,
    advancePaymentRequired,
    choices: resolvePortalBookingPaymentChoices({
      methods: input.methods,
      applicablePunchCards: input.applicablePunchCards,
      requiresPrepayment: advancePaymentRequired,
      chargeAmountCents,
    }),
  };
}

export function resolveStaffPaymentChoices(input: {
  methods: BookingPaymentMethods;
  applicablePunchCards: Array<{ remaining_clips: number }>;
  mobilePayAvailable: boolean;
  chargeAmountCents: number;
  includeDeskMethods?: boolean;
  canRedeemPunchCard?: boolean;
  canSettleInvoice?: boolean;
}): StaffPaymentChoice[] {
  const options = resolvePaymentOptions({
    methods: input.methods,
    audience: 'staff',
    clinioPayAvailable: input.mobilePayAvailable,
    canRedeemPunchCard: input.canRedeemPunchCard,
    canSettleInvoice: input.canSettleInvoice,
    canRecordManualPayment: input.includeDeskMethods !== false,
    applicablePunchCards: input.applicablePunchCards,
    chargeAmountCents: input.chargeAmountCents,
  });
  const choices: StaffPaymentChoice[] = [];
  if (options.punchCard) choices.push('punch_card');
  const order = ['card', 'mobilepay', 'payment_link', 'cash', 'bank', 'invoice'] as const;
  for (const method of order) {
    if (!options.monetaryMethods.includes(method)) continue;
    choices.push(method === 'invoice' ? 'invoice_email' : method);
  }
  return choices;
}

export function shouldOfferStaffPaymentChoice(input: {
  mode: 'create' | 'edit';
  isBlocker: boolean;
  isRecurring: boolean;
  hasPatient: boolean;
  availableChoices: StaffPaymentChoice[];
}): boolean {
  if (input.mode !== 'create') return false;
  if (input.isBlocker || input.isRecurring || !input.hasPatient) return false;
  return input.availableChoices.length > 0;
}

/** The create-flow payment window is a confirmation gate, not a post-save extra. */
export function shouldDeferStaffCreateUntilPaymentStep(input: {
  offerPaymentChoice: boolean;
}): boolean {
  return input.offerPaymentChoice;
}

/** Pay-at-appointment clinics can confirm the booking without settling now. */
export function shouldAllowConfirmWithoutPayment(input: {
  paymentRequired: boolean;
}): boolean {
  return !input.paymentRequired;
}

/**
 * Confirmation email only after the appointment actually exists and, when the
 * payment window is part of create, after that step is completed or skipped.
 */
export function shouldSendStaffAppointmentConfirmation(input: {
  hasPatient: boolean;
  appointmentCreated: boolean;
  paymentStepDeferred: boolean;
  paymentStepCompleted: boolean;
}): boolean {
  if (!input.hasPatient || !input.appointmentCreated) return false;
  if (input.paymentStepDeferred) return input.paymentStepCompleted;
  return true;
}

/** Staff checkbox (default on) plus per-patient opt-out around the payment-step gate. */
export function shouldSendStaffAppointmentEmail(input: {
  hasPatient: boolean;
  appointmentCreated: boolean;
  paymentStepDeferred: boolean;
  paymentStepCompleted: boolean;
  staffWantsEmail: boolean;
  patientOptedOut: boolean;
}): boolean {
  if (!input.staffWantsEmail || input.patientOptedOut) return false;
  return shouldSendStaffAppointmentConfirmation(input);
}

export function isStaffPaymentChoiceRequired(input: {
  paymentTiming: PaymentTiming;
  chargeAmountCents: number;
}): boolean {
  return resolvePrepaymentRequired({
    paymentTiming: input.paymentTiming,
    priceCents: input.chargeAmountCents,
  });
}

export function resolveStaffPaymentContext(input: {
  clinicPaymentTiming?: unknown;
  servicePaymentTiming?: unknown;
  serviceRequirePrepayment?: boolean | null;
  depositAmountCents?: number | null;
  priceCents?: number | null;
}): { paymentTiming: PaymentTiming; chargeAmountCents: number; required: boolean } {
  const paymentTiming = resolvePaymentTiming({
    clinicPaymentTiming: input.clinicPaymentTiming,
    servicePaymentTiming: input.servicePaymentTiming,
    serviceRequirePrepayment: input.serviceRequirePrepayment,
  });
  const chargeAmountCents = resolveChargeAmountCents({
    depositAmountCents: input.depositAmountCents,
    priceCents: input.priceCents,
  });
  return {
    paymentTiming,
    chargeAmountCents,
    required: isStaffPaymentChoiceRequired({ paymentTiming, chargeAmountCents }),
  };
}
